import { Capacitor } from "@capacitor/core";
import { Health } from "@krzysztofkostecki/capacitor-health";
import api from "@/lib/api";

const READ = ["sleep", "heartRateVariability", "restingHeartRate", "steps"];
const ASLEEP = ["asleep", "rem", "deep", "light"];
const GAP_MS = 90 * 60 * 1000;

export const isNativeApp = () => Capacitor.isNativePlatform();
export const nativePlatform = () => Capacitor.getPlatform();
export const healthLabel = () => (Capacitor.getPlatform() === "android" ? "Health Connect" : "Apple Health");

const iso = (d) => d.toISOString();
const hoursAgo = (h) => new Date(Date.now() - h * 3600 * 1000);
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const avg = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

async function read(dataType, startDate, limit) {
  try {
    const res = await Health.readSamples({ dataType, startDate: iso(startDate), endDate: iso(new Date()), limit, ascending: true });
    return res?.samples || [];
  } catch {
    return [];
  }
}

/** Duration of the most recent sleep session (sessions split by gaps > 90 min). */
function lastNightHours(samples) {
  const asleep = samples.filter((s) => !s.sleepState || ASLEEP.includes(s.sleepState));
  if (!asleep.length) return null;
  const sessions = [[]];
  for (const s of asleep) {
    const cur = sessions[sessions.length - 1];
    const prev = cur[cur.length - 1];
    if (prev && new Date(s.startDate) - new Date(prev.endDate) > GAP_MS) sessions.push([]);
    sessions[sessions.length - 1].push(s);
  }
  const last = sessions[sessions.length - 1];
  const ms = last.reduce((t, s) => t + (new Date(s.endDate) - new Date(s.startDate)), 0);
  const hours = Math.round((ms / 3600000) * 10) / 10;
  return hours > 0 ? hours : null;
}

export async function readHealthSnapshot() {
  const [sleep, hrv, rhr, steps] = await Promise.all([
    read("sleep", hoursAgo(36), 300),
    read("heartRateVariability", hoursAgo(36), 50),
    read("restingHeartRate", hoursAgo(48), 20),
    read("steps", startOfToday(), 500),
  ]);
  const data = {};
  const hours = lastNightHours(sleep);
  if (hours) data.sleep_hours = hours;
  const h = avg(hrv.map((s) => s.value).filter(Boolean));
  if (h) data.hrv = Math.round(h);
  if (rhr.length) data.resting_hr = Math.round(rhr[rhr.length - 1].value);
  const total = steps.reduce((t, s) => t + (s.value || 0), 0);
  if (total) data.steps = Math.round(total);
  return data;
}

async function push(data) {
  const { data: res } = await api.post("/wearables/native/sync", { platform: Capacitor.getPlatform(), data });
  return res;
}

/** First-run: asks for the OS permission sheet, then sends a first snapshot. */
export async function connectHealth() {
  const avail = await Health.isAvailable();
  if (!avail?.available) throw new Error(`${healthLabel()} isn't available on this device`);
  const status = await Health.requestAuthorization({ read: READ });
  if (!status?.readAuthorized?.length) throw new Error(`No ${healthLabel()} permissions were granted`);
  const data = await readHealthSnapshot();
  await push(data);
  return data;
}

/** Background refresh — silent, only when permissions already exist. */
export async function syncHealth() {
  if (!isNativeApp()) return null;
  const status = await Health.checkAuthorization({ read: READ }).catch(() => null);
  if (!status?.readAuthorized?.length) return null;
  const data = await readHealthSnapshot();
  if (!Object.keys(data).length) return null;
  await push(data);
  return data;
}
